// Amplitude user properties describing where inside the tenant the admin is working.
//
// The logged user object only carries the tenant and the role: the selected term/stage are
// session state that AuthService keeps in localStorage (the ids as raw keys, the terms as a
// JSON array), so they are resolved from that same storage instead of being threaded through
// every caller — `identifyUser` and `refreshSelection` read the exact same source.
//
// Both the readable name and the mongo id are reported: the name is what makes an Amplitude
// chart legible, the id is what makes a row traceable back to the record.

import { Term } from '../../interfaces'
import { StoreService } from '../../services/store.service'

export interface SelectionProperties {
  stage?: string
  stage_id?: string
  term?: string
  term_id?: string
}

// Written by AuthService; read here without importing it (AuthService imports this module
// through AnalyticsService, so the dependency only goes one way).
const TERMS_KEY = 'terms'
const TERM_KEY = 'term'
const STAGE_KEY = 'stage'

export function buildSelectionProperties (store: StoreService): SelectionProperties {
  try {
    const termId = store.getItem(TERM_KEY) || undefined
    const stageId = store.getItem(STAGE_KEY) || undefined
    const term = termId ? readTerms(store).find(item => item._id === termId) : undefined
    const stage = term && term.stages && stageId
      ? term.stages.find(item => item._id === stageId)
      : undefined

    // an id that no longer resolves is still worth reporting (stale session, term rollover);
    // the name is not, so it is only added when the record was actually found
    return {
      stage: stage && stage.name,
      stage_id: stageId,
      term: term && term.name,
      term_id: termId
    }
  } catch (e) {
    // tracking is best effort: a broken/unavailable storage must never break identifyUser
    return {}
  }
}

function readTerms (store: StoreService): Term[] {
  const stored = store.getJson(TERMS_KEY)
  return Array.isArray(stored) ? stored : []
}
